import React, { useCallback, useEffect, useState } from 'react';
import { X, Sparkles, Loader2, Save, Send, RefreshCw, FileText, Copy, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import StatusChip from '@/components/recovery/StatusChip';
import { backendUrl, errMsg } from '@/components/recovery/recoveryUi';
import RecoveryPlanEditor from './RecoveryPlanEditor';
import MedicineListEditor from './MedicineListEditor';
import RecoveryDetail from './RecoveryDetail';

const NAVY = '#07122f';
const TEAL = '#007f73';
const btn = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-bold disabled:opacity-50';

function ShareBox({ planId, waText }) {
  const link = `${window.location.origin}/recovery/${planId}`;
  const text = (waText || 'Your recovery plan: {LINK}\n\nOpen this link, tap ALLOW NOTIFICATIONS so your surgical team can reach you instantly, and keep the page open to get medicine reminders.').replace('{LINK}', link);
  return (
    <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50 p-3" data-testid="rec-share-box">
      <div className="flex items-center gap-2"><input readOnly value={link} className="flex-1 bg-transparent text-[12px] outline-none" data-testid="rec-patient-link" />
        <button type="button" onClick={() => { navigator.clipboard?.writeText(link); toast.success('Link copied'); }} data-testid="rec-copy-link"><Copy size={14} /></button></div>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" className={`${btn} mt-2 text-white`} style={{ background: '#16a34a' }} data-testid="rec-share-whatsapp">Send on WhatsApp</a>
    </div>
  );
}

export default function RecoveryPanel({ dischargeId, sealed, onClose }) {
  const [plan, setPlan] = useState(undefined);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState('');
  const [mobile, setMobile] = useState('');
  const [waText, setWaText] = useState('');
  const [revise, setRevise] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/surgical/recovery/by-discharge/${dischargeId}`);
      setPlan(data.plan); setEdit(data.plan?.status === 'draft' ? data.plan.plan : null);
    } catch { setPlan(null); }
  }, [dischargeId]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e, 'Something went wrong')); } finally { setBusy(''); }
  };
  const draft = (regenerate) => run('draft', async () => {
    const { data } = await api.post(`/surgical/recovery/draft/${dischargeId}`, { regenerate });
    setPlan(data); setEdit(data.plan);
  }, 'Draft ready — review every medicine before publishing');
  const save = () => run('save', async () => { const { data } = await api.put(`/surgical/recovery/${plan.id}`, { plan: edit }); setPlan(data); setEdit(data.plan); }, 'Draft saved');
  const publish = () => run('publish', async () => {
    await api.put(`/surgical/recovery/${plan.id}`, { plan: edit });
    const { data } = await api.post(`/surgical/recovery/${plan.id}/publish`, { patient_mobile: mobile });
    setWaText(data.whatsapp_text); await load();
  }, 'Published — discharge Rx issued');
  const doRevise = () => run('revise', async () => { await api.post(`/surgical/recovery/${plan.id}/revise-rx`, { medicines: revise }); setRevise(null); await load(); }, 'Revised Rx issued — previous one superseded');
  const close = () => { if (window.confirm('Mark recovery complete and close this plan?')) run('close', async () => { await api.post(`/surgical/recovery/${plan.id}/close`); await load(); }, 'Plan closed'); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl my-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rec-panel">
        <div className="px-5 py-3 flex items-center gap-3 rounded-t-2xl text-white" style={{ background: NAVY }}>
          <div className="flex-1"><div className="text-[10px] font-bold uppercase tracking-[.2em] opacity-70">Surgical Suite</div><div className="text-[15px] font-black">Recovery Care Plan & Discharge Rx</div></div>
          {plan && <StatusChip status={plan.status === 'draft' ? 'draft' : plan.status_chip || plan.status} testId="rec-panel-status" />}
          <button type="button" onClick={onClose} data-testid="rec-panel-close"><X size={18} /></button>
        </div>
        <div className="p-5">
          {plan === undefined && <div className="py-8 text-center"><Loader2 className="inline animate-spin" /></div>}
          {plan === null && (
            <div className="py-8 text-center" data-testid="rec-empty">
              <p className="text-[13px] text-slate-600 max-w-md mx-auto">One click drafts the recovery plan (milestones, do's & don'ts, red flags, check-ins) and the structured discharge medicine list from this sealed summary.</p>
              <button type="button" disabled={!sealed || busy === 'draft'} onClick={() => draft(false)} className={`${btn} mt-4 text-white`} style={{ background: TEAL }} data-testid="rec-create-draft">
                {busy === 'draft' ? <><Loader2 size={14} className="animate-spin" /> Drafting with AI (≈10–30 s)…</> : <><Sparkles size={14} /> Create Recovery Plan</>}
              </button>
              {!sealed && <div className="mt-2 text-[12px] text-amber-700">Seal the discharge summary first.</div>}
            </div>
          )}
          {plan?.status === 'draft' && edit && (
            <>
              <RecoveryPlanEditor plan={edit} onChange={setEdit} />
              <div className="mt-5 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-2">
                <button type="button" onClick={save} disabled={!!busy} className={`${btn} border border-slate-300`} data-testid="rec-save-draft">{busy === 'save' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save draft</button>
                <button type="button" onClick={() => draft(true)} disabled={!!busy} className={`${btn} border border-slate-300`} data-testid="rec-regenerate">{busy === 'draft' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate (AI)</button>
                <div className="flex-1" />
                <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Patient mobile (10 digits)" className="px-3 py-2 rounded-lg border border-slate-300 text-[13px] w-52" data-testid="rec-publish-mobile" />
                <button type="button" onClick={publish} disabled={!!busy || mobile.replace(/\D/g, '').length < 10} className={`${btn} text-white`} style={{ background: NAVY }} data-testid="rec-publish">{busy === 'publish' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Publish & issue Rx</button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500 text-right">Principal Surgeon only · residents can draft and edit.</p>
            </>
          )}
          {plan && plan.status !== 'draft' && (
            <>
              <div className="flex flex-wrap gap-2 items-center">
                {plan.current_rx_id && <a href={backendUrl(`/api/rx/issued/${plan.current_rx_id}/pdf`)} target="_blank" rel="noopener noreferrer" className={`${btn} text-white`} style={{ background: TEAL }} data-testid="rec-open-rx"><FileText size={13} /> Discharge Rx</a>}
                {plan.status === 'published' && <button type="button" onClick={() => setRevise(plan.plan.medicines)} className={`${btn} border border-slate-300`} data-testid="rec-revise-open"><RefreshCw size={13} /> Revise Rx</button>}
                {plan.status === 'published' && <button type="button" onClick={close} className={`${btn} border border-slate-300`} data-testid="rec-close-plan"><Lock size={13} /> Mark recovery complete</button>}
              </div>
              {plan.status === 'published' && <ShareBox planId={plan.id} waText={waText} />}
              {revise && (
                <div className="mt-4 rounded-xl border-2 border-amber-300 p-3" data-testid="rec-revise-box">
                  <div className="text-[12px] font-bold mb-2">Revised medicine list — the current Rx will be marked “superseded”.</div>
                  <MedicineListEditor meds={revise} onChange={setRevise} testPrefix="rec-revise-med" />
                  <div className="mt-3 flex gap-2"><button type="button" onClick={doRevise} disabled={!!busy} className={`${btn} text-white`} style={{ background: NAVY }} data-testid="rec-revise-submit">{busy === 'revise' && <Loader2 size={13} className="animate-spin" />} Issue revised Rx</button>
                    <button type="button" onClick={() => setRevise(null)} className={`${btn} border border-slate-300`}>Cancel</button></div>
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-slate-200"><RecoveryDetail key={`${plan.id}-${plan.current_rx_id}-${plan.status}`} planId={plan.id} /></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
